import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import { Activity, Brain, ClipboardCheck, Filter, ChevronRight, Target, Timer, Zap } from "lucide-react";

import { BRAND_MODERATION_PAGE_SIZE, ConsumerResearchApiFactory, type BrandModerationActionLogItem, type BrandModerationActionMetricsData } from "@/apis/research/consumer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatDate } from "@/lib/utils";

import { mRuleFilters, mReplyTypes, type ModerationFeedCard, type ReplyTypeId } from "@/mock/ai-consumer-research";
import { upperFirst } from "lodash";

/** Referenced when the day/rule toolbar JSX below is uncommented; keeps imports live for `noUnusedLocals`. */
void [Filter, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, TabsList, TabsTrigger, Separator];

type ActionHistoryStatId = "total_actions" | "auto_responded" | "avg_confidence" | "avg_response" | "review_rate";

const ACTION_HISTORY_STATS: { id: ActionHistoryStatId; label: string }[] = [
  { id: "total_actions", label: "Total Actions" },
  { id: "auto_responded", label: "Auto-Responded" },
  { id: "avg_confidence", label: "Avg Confidence" },
  { id: "avg_response", label: "Avg Response" },
  { id: "review_rate", label: "Review Rate" },
];

function formatActionMetricsPct(n: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 1, minimumFractionDigits: 0 }).format(n);
}

function formatActionMetricsResponseMin(min: number): string {
  if (min <= 0) return "0s";
  if (min < 1) return `${Math.round(min * 60)}s`;
  const rounded = Math.round(min * 10) / 10;
  return `${Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)}m`;
}

function formatActionHistoryStatValue(id: ActionHistoryStatId, metrics: BrandModerationActionMetricsData | null | undefined): string {
  if (!metrics) return "—";
  switch (id) {
    case "total_actions":
      return metrics.total_actions != null ? String(Math.round(metrics.total_actions)) : "—";
    case "auto_responded":
      return metrics.auto_responded_pct != null ? `${formatActionMetricsPct(metrics.auto_responded_pct)}%` : "—";
    case "avg_confidence":
      return metrics.avg_confidence_pct != null ? `${formatActionMetricsPct(metrics.avg_confidence_pct)}%` : "—";
    case "avg_response":
      return metrics.avg_response_min != null ? formatActionMetricsResponseMin(metrics.avg_response_min) : "—";
    case "review_rate":
      return metrics.review_rate_pct != null ? `${formatActionMetricsPct(metrics.review_rate_pct)}%` : "—";
    default:
      return "—";
  }
}

const actionHistoryStatIcons: Record<ActionHistoryStatId, LucideIcon> = {
  total_actions: Activity,
  auto_responded: Zap,
  avg_confidence: Target,
  avg_response: Timer,
  review_rate: ClipboardCheck,
};

const actionHistoryStatIconClass: Record<ActionHistoryStatId, { wrap: string; icon: string }> = {
  total_actions: { wrap: "bg-sky-500/10", icon: "text-sky-600" },
  auto_responded: { wrap: "bg-success/10", icon: "text-success" },
  avg_confidence: { wrap: "bg-violet-500/10", icon: "text-violet-600" },
  avg_response: { wrap: "bg-amber-500/10", icon: "text-amber-600" },
  review_rate: { wrap: "bg-blue-500/10", icon: "text-blue-600" },
};

const dayFilters = ["Today", "7 days", "30 days"] as const;
void dayFilters;

export type DayFilter = (typeof dayFilters)[number];
export type CommentTypeValue = (typeof mReplyTypes)[number]["id"];
export type RuleFilterValue = (typeof mRuleFilters)[number]["value"];

function titleCasePlatform(platform: string) {
  if (!platform) return "";
  return platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase();
}

function mapApiPriority(priority: string | undefined): ModerationFeedCard["priority"] {
  const p = (priority ?? "").toLowerCase();
  if (p === "high") return "high";
  if (p === "low") return "low";
  return "medium";
}

function mapActionLogItemToFeedCard(item: BrandModerationActionLogItem): ModerationFeedCard {
  const name = item.comment_user_name ?? "";
  const handle = name.startsWith("@") ? name : name ? `@${name}` : "@—";
  const confRaw = item.confidence_score;
  const confidencePct = typeof confRaw === "number" ? Math.min(100, Math.max(0, Math.round(confRaw * 100))) : 0;
  const category = item.category ?? "";
  const categoryLabel = category ? category.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—";

  const statusLower = (item.status ?? "").toLowerCase();
  let status = "needs_review";
  if (statusLower.includes("flag")) status = "flag";
  else if (statusLower.includes("auto") || statusLower.includes("respond")) status = "auto_responded";

  let replyType: ReplyTypeId = "auto_responded";
  const ra = (item.resolved_action ?? item.actioned_type ?? "").toLowerCase();
  if (ra.includes("flag")) replyType = "flagged";
  else if (ra.includes("reject")) replyType = "rejected";
  else if (ra.includes("hide")) replyType = "hidden";
  else if (ra.includes("review") || ra.includes("sent")) replyType = "sent_after_review";

  const ts = item.actioned_at ?? item.resolved_at ?? item.updated_at ?? item.created_at ?? "";
  const lastSync = ts ? formatDate(ts, "MMM d, yyyy · h:mm a") : "—";
  const content = item.comment_text ?? item.post_text ?? "—";

  let actionTaken = item.action_taken?.toLowerCase() ?? "-";
  switch (actionTaken) {
    case "flag":
      actionTaken = "Flagged";
      break;
    case "reject":
      actionTaken = "Rejected";
      break;
    case "hide":
      actionTaken = "Hidden";
      break;
    case "review_needed":
      actionTaken = "Needs Review";
      break;
    case "send":
      actionTaken = "Sent";
      break;
    case "assign":
      actionTaken = "Assigned";
      break;
  }

  return {
    platform: titleCasePlatform(item.platform ?? ""),
    subBrand: item.brand_name ?? "—",
    sourceDescription: categoryLabel,
    userHandle: handle,
    rating: 0,
    content,
    rule: item.reasoning ? item.reasoning?.replace("Rule matched:", "").replaceAll("_", " ") : "—",
    confidence: confidencePct,
    status,
    priority: mapApiPriority(item.priority),
    lastSync,
    whyReason: item.matched_rule_summary?.trim() ? item.matched_rule_summary : categoryLabel,
    trigger: item.matched_rule_summary ?? "—",
    playbookApplied: item.playbook_name || "—",
    aiDraft: item.ai_draft_response,
    aiResponse: false,
    replyType,
    ruleFilter: "all",
    actionTaken: actionTaken,
  };
}

type ActionLogsPageMeta = {
  page: number;
  size: number;
  total: number;
  pages: number;
};

function ActionLogsPaginationBar({ meta, isFetching, onPageChange }: { meta: ActionLogsPageMeta | undefined; isFetching: boolean; onPageChange: (page: number) => void }) {
  if (!meta || meta.total === 0) return null;

  const { page, pages } = meta;
  const totalPages = Math.max(1, pages);
  const canPrev = page > 1 && !isFetching;
  const canNext = page < totalPages && !isFetching;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 mt-2">
      <div className="flex items-center justify-center sm:justify-end gap-2 ml-auto">
        <Button variant="outline" size="sm" className="rounded-md text-xs" disabled={!canPrev} onClick={() => onPageChange(page - 1)}>
          Previous
        </Button>
        <span className="text-xs text-muted-foreground tabular-nums min-w-[5.5rem] text-center">
          Page {page} of {totalPages}
        </span>
        <Button variant="outline" size="sm" className="rounded-md text-xs" disabled={!canNext} onClick={() => onPageChange(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}

function TabEmpty({ message }: { message: string }) {
  return <p className="text-sm text-muted-foreground py-6 text-center">{message}</p>;
}

function ActionHistoryCardSkeleton() {
  return (
    <Card className="rounded-xl border shadow-none overflow-hidden pb-2">
      <CardContent className="space-y-3 p-0 px-4 py-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Skeleton className="h-5 w-[5.5rem] rounded-sm" />
            <Skeleton className="h-4 w-14" />
            <Skeleton className="h-3 w-24 max-w-[40%]" />
            <Skeleton className="h-5 w-[4.5rem] rounded-sm" />
          </div>
          <Skeleton className="h-3 w-28 shrink-0 sm:ml-auto" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-[94%]" />
          <Skeleton className="h-4 w-[78%]" />
        </div>
        <div className="flex items-center justify-end pt-0.5">
          <Skeleton className="h-3 w-32" />
        </div>
      </CardContent>
    </Card>
  );
}

function ActionHistoryListSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading action history">
      {Array.from({ length: count }, (_, i) => (
        <ActionHistoryCardSkeleton key={i} />
      ))}
    </div>
  );
}

function ApiActionLogsCardList({ items }: { items: BrandModerationActionLogItem[] }) {
  return (
    <div className="space-y-4">
      {items.map((item) => (
        <HistoryCard key={item.id} card={mapActionLogItemToFeedCard(item)} />
      ))}
    </div>
  );
}

export function ActionHistory() {
  // const [ruleFilter, setRuleFilter] = useState<RuleFilterValue>("all");
  const [actionLogsPage, setActionLogsPage] = useState(1);

  const {
    data: actionLogsResponse,
    isLoading: actionLogsLoading,
    isFetching: actionLogsFetching,
  } = useQuery({
    ...ConsumerResearchApiFactory.Queries.ActionLogs({ page: actionLogsPage, size: BRAND_MODERATION_PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const actionLogItems = actionLogsResponse?.data.items ?? [];
  const actionLogsMeta = actionLogsResponse?.data;

  return (
    <div className="space-y-6">
      <Stats />
      <Tabs defaultValue="total" className="space-y-4">
        {/* <div className="overflow-x-auto scrollbar-hidden">
          <div className="flex items-center gap-3 w-fit">
            <Tabs defaultValue="Today" className="flex-shrink-0">
              <TabsList className="bg-transparent gap-2 p-0 flex-nowrap">
                {dayFilters.map((day) => (
                  <TabsTrigger
                    key={day}
                    value={day}
                    className="rounded-full border border-border bg-background px-4 py-1.5 text-sm font-medium text-muted-foreground data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:border-foreground data-[state=active]:shadow-none whitespace-nowrap"
                  >
                    {day}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <Separator orientation="vertical" className="data-[orientation=vertical]:h-6" />

            <TabsList className="bg-transparent gap-2 p-0 flex-nowrap">
              {orderedTabs.map(({ id, label }) => (
                <TabsTrigger
                  key={id}
                  value={id}
                  className="rounded-full border border-border bg-background px-4 py-1.5 text-sm font-medium text-muted-foreground data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:border-foreground data-[state=active]:shadow-none whitespace-nowrap"
                >
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>

            <Separator orientation="vertical" className="data-[orientation=vertical]:h-6" />

            <Select value={ruleFilter} onValueChange={(value) => setRuleFilter(value as RuleFilterValue)}>
              <SelectTrigger className="w-48 truncate bg-white rounded-lg">
                <SelectValue placeholder="Rules" />
              </SelectTrigger>
              <SelectContent>
                {mRuleFilters.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" className="rounded-lg bg-background">
              <Filter className="size-4" />
              More Filter
            </Button>
          </div>
        </div> */}

        <TabsContent value="total" className="mt-0">
          {actionLogsLoading && !actionLogsResponse ? (
            <ActionHistoryListSkeleton />
          ) : (actionLogsMeta?.total ?? 0) === 0 ? (
            <TabEmpty message="No action history yet." />
          ) : (
            <>
              {actionLogItems.length === 0 ? <TabEmpty message="No items on this page." /> : <ApiActionLogsCardList items={actionLogItems} />}
              <ActionLogsPaginationBar meta={actionLogsMeta} isFetching={actionLogsFetching} onPageChange={setActionLogsPage} />
            </>
          )}
        </TabsContent>

        {/* {(["auto_responded", "sent_after_review", "rejected", "flagged", "hidden"] as ReplyTypeId[]).map((replyType) => (
          <TabsContent key={replyType} value={replyType} className="mt-0">
            <CardList cards={filteredCards.filter((c) => c.replyType === replyType)} />
          </TabsContent>
        ))} */}
      </Tabs>
    </div>
  );
}

function HistoryCard({ card }: { card: ModerationFeedCard }) {
  const replyTypeStyles: Record<ReplyTypeId, string> = {
    auto_responded: "text-success bg-success/10 border border-success/20",
    sent_after_review: "text-blue-600 bg-blue-50 border border-blue-600/20",
    rejected: "text-destructive bg-destructive/10 border border-destructive/20",
    flagged: "text-foreground bg-foreground/10 border border-foreground/20",
    hidden: "text-warning bg-warning/10 border border-warning/20",
  };

  const [open, setOpen] = useState(false);
  // const replyType = mReplyTypes.find((type) => type.id === card.replyType);
  const replyTypeStyle = replyTypeStyles[card.actionTaken as ReplyTypeId] || "text-foreground bg-muted";

  return (
    <Card className="rounded-xl border shadow-none overflow-hidden pb-2">
      <CardContent className="p-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 px-4 pb-1">
          <div className="flex items-center gap-2 min-w-0">
            <Badge className={cn("rounded-sm text-xs font-medium px-2 py-0.5 border-0", replyTypeStyle)}>{upperFirst(card.actionTaken?.toLowerCase())}</Badge>
            <span className="font-semibold text-sm text-foreground">{card.platform}</span>
            <span className="text-xs text-muted-foreground truncate">{card.userHandle}</span>
            <Badge variant="secondary" className="rounded-sm text-xs px-2 py-0.5">
              {card.subBrand}
            </Badge>
          </div>
          <span className="shrink-0 text-xs text-muted-foreground/80">{card.lastSync}</span>
        </div>
        {card.aiResponse ? (
          <div className="px-4 py-3">
            <div className="p-3 card rounded-md bg-muted">
              <p className="text-xs text-muted-foreground leading-relaxed"> AI Response</p>
              <p className="text-sm text-foreground leading-relaxed">{card.content}</p>
            </div>
          </div>
        ) : (
          <div className="px-4 pb-3">
            <p className="text-sm text-foreground leading-relaxed">{card.content}</p>
          </div>
        )}

        <Collapsible open={open} onOpenChange={setOpen}>
          <div className="flex items-center justify-between px-4 py-2">
            {/* <span className="text-xs text-muted-foreground">Rule: {card.rule}</span> */}

            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                <Brain className="size-4" />
                Agent Reasoning
                <ChevronRight className={cn("size-4 transition-transform", open && "rotate-90")} />
              </button>
            </CollapsibleTrigger>
          </div>

          <CollapsibleContent>
            <div className="mt-3 rounded-lg bg-muted/40 space-y-4 text-xs px-5 py-2">
              <div className="flex gap-8">
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">Rule Matched</p>
                  <p className="text-sm foreground/80">{card.rule}</p>
                </div>
                <div className="w-md">
                  <p className="font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">Confidence</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                      <div className="bg-success h-full" style={{ width: `${card.confidence}%` }} />
                    </div>
                    <span className="text-foreground font-semibold">{card.confidence}%</span>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">Playbook Applied</p>
                <p className="text-sm foreground/80">{card.playbookApplied}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">Action Taken</p>
                <p className="text-sm foreground/80">{card.actionTaken}</p>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}

function Stats() {
  const { data: actionMetricsResponse, isPending } = useQuery({
    ...ConsumerResearchApiFactory.Queries.ActionMetrics(),
    placeholderData: keepPreviousData,
  });

  const actionMetrics = actionMetricsResponse?.data ?? undefined;

  return (
    <div className="flex gap-3 overflow-x-auto scrollbar-hidden pb-4 -mx-1 px-1 border-b border-dashed">
      {ACTION_HISTORY_STATS.map(({ id, label }) => {
        const Icon = actionHistoryStatIcons[id];
        const tone = actionHistoryStatIconClass[id];
        const value = isPending ? null : formatActionHistoryStatValue(id, actionMetrics);
        return (
          <Card key={id} className="min-w-[9.5rem] flex-1 shrink-0 rounded-xl border shadow-none">
            <CardContent className="relative">
              <div className="flex items-start justify-between gap-3 h-full ">
                <div className="min-w-0">
                  {value === null ? <Skeleton className="h-8 w-16 mb-0.5" /> : <p className="text-2xl font-semibold text-foreground tabular-nums">{value}</p>}
                  <p className="text-xs uppercase tracking-wide text-muted-foreground mt-0.5 leading-snug">{label}</p>
                </div>
                <div className={cn("rounded-lg p-2 shrink-0 absolute -top-2 right-4", tone.wrap)} aria-hidden>
                  <Icon className={cn("size-5", tone.icon)} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
